import { PRODUCT_NAME } from "@mailhand/core/brand";
import { useState } from "react";
import { Segmented } from "../../ui/index.js";

const PROVIDERS = ["gmail", "workspace", "outlook", "other"] as const;
type Provider = (typeof PROVIDERS)[number];

const PROVIDER_LABEL: Record<Provider, string> = {
  gmail: "Gmail",
  workspace: "Google Workspace",
  outlook: "Outlook",
  other: "Other",
};

function steps(provider: Provider, address: string): string[] {
  switch (provider) {
    case "gmail":
      return [
        "Open Gmail on a computer and sign in to your support mailbox.",
        "Click the gear, then See all settings, then the Forwarding and POP/IMAP tab.",
        `Click Add a forwarding address and paste ${address}.`,
        "Gmail sends a confirmation to that address. The code appears below; click Confirm or paste the code.",
        "Back in Forwarding, choose Forward a copy of incoming mail, keep Gmail's copy in the inbox, and save.",
      ];
    case "workspace":
      return [
        "Open the Google Admin console as an administrator.",
        "Go to Apps, Google Workspace, Gmail, then Routing.",
        "Add a routing rule for the support mailbox (or a group) and choose Change envelope recipient, Add more recipients.",
        `Add ${address} as a recipient and keep delivery to the original mailbox.`,
        "Save. No confirmation code is needed; send a test email in step 3.",
      ];
    case "outlook":
      return [
        "Open Outlook on the web and sign in to your support mailbox.",
        "Open Settings, then Mail, then Rules, and add a new rule.",
        `Name it ${PRODUCT_NAME} and set the condition to Apply to all messages.`,
        `Choose the action Forward to and enter ${address}.`,
        "Save the rule. No confirmation code is needed; send a test email in step 3.",
      ];
    case "other":
      return [
        "Sign in to your email host's settings for the support mailbox.",
        "Find Forwarding, Filters, or Rules.",
        `Forward a copy of incoming mail to ${address}.`,
        "Keep a copy in your own mailbox, so nothing changes for you.",
        `If your host sends a confirmation email, it shows up below. Otherwise send a test email to see ${PRODUCT_NAME} receive it.`,
      ];
  }
}

export function ForwardingGuide({ address }: { address: string }) {
  const [provider, setProvider] = useState<Provider>("gmail");
  return (
    <div className="guide">
      <Segmented label="Email provider" value={provider} options={PROVIDERS.map((value) => ({ value, label: PROVIDER_LABEL[value] }))} onChange={setProvider} />
      <ol className="guide-steps">
        {steps(provider, address).map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ol>
    </div>
  );
}
